/**
 * Inference API Client
 *
 * Handles all P3 inference router API calls:
 * - Provider Management
 * - Inference Routing
 * - Privacy Policies
 *
 * Copyright (c) 2026 Equilateral AI. All rights reserved.
 * Follows: .equilateral-standards/serverless-saas-aws/api_standards.md
 */

import { Make_Authorized_API_Call, type TimComboAPIResponse } from './apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

export type { TimComboAPIResponse };

// =============================================================================
// TYPES
// =============================================================================

export interface InferenceProvider {
  Provider_ID: string;
  Provider_Name: string;
  Provider_Type: 'local' | 'cloud' | 'hybrid';
  Privacy_Level: string;
  Supported_Models: string[];
  Latency_P50_Ms: number;
  Cost_Per_1k_Tokens: number;
  Max_Context_Tokens: number;
  Capabilities: string[];
  Enabled: boolean;
}

export interface RoutingDecision {
  Decision_ID: string;
  Task_Type: string;
  Selected_Provider_Name: string;
  Privacy_Requirement: string;
  Data_Classification: 'public' | 'internal' | 'confidential' | 'restricted';
  Scrubbing_Applied: boolean;
  Scrubbed_Fields: string[];
  Routing_Factors: Record<string, unknown>;
  Fallback_Used: boolean;
  Routed_At: string;
}

export interface PrivacyPolicy {
  Policy_ID: string;
  Policy_Name: string;
  Data_Classification: 'public' | 'internal' | 'confidential' | 'restricted';
  Allowed_Provider_Types: string[];
  Require_Scrubbing: boolean;
  Scrub_Patterns: string[];
  Max_Cost_Per_Request: number;
}

// =============================================================================
// PROVIDERS
// =============================================================================

export async function getProviders(
  clientId: string,
  opts: { Provider_Type?: string; Enabled?: boolean } = {}
): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.INFERENCE.PROVIDERS,
    'GET',
    null,
    { params: { Client_ID: clientId, ...opts } }
  );
}

export async function updateProviderConfig(
  clientId: string,
  data: {
    Provider_ID: string;
    Latency_P50_Ms?: number;
    Cost_Per_1k_Tokens?: number;
    Enabled?: boolean;
  }
): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.INFERENCE.PROVIDER_CONFIG,
    'PUT',
    { Client_ID: clientId, ...data }
  );
}

// =============================================================================
// ROUTING
// =============================================================================

export async function routeInference(
  clientId: string,
  data: {
    Task_Type: string;
    Data_Classification: string;
    Privacy_Requirement?: string;
    Preferred_Provider?: string;
  }
): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.INFERENCE.ROUTE,
    'POST',
    { Client_ID: clientId, ...data }
  );
}

export async function getRoutingHistory(
  clientId: string,
  opts: {
    Provider_Name?: string;
    Data_Classification?: string;
    From_Date?: string;
    To_Date?: string;
    limit?: number;
    offset?: number;
  } = {}
): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.INFERENCE.ROUTING_HISTORY,
    'GET',
    null,
    { params: { Client_ID: clientId, ...opts } }
  );
}

// =============================================================================
// PRIVACY POLICIES
// =============================================================================

export async function getPrivacyPolicies(
  clientId: string,
  opts: { Data_Classification?: string } = {}
): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.INFERENCE.PRIVACY_POLICIES,
    'GET',
    null,
    { params: { Client_ID: clientId, ...opts } }
  );
}
